import BaseAsr from "../baseAsr";
import AudioMedia from "../../utils/audioMedia";
import Timer from "../../utils/timer";
import { logMimiAsr } from "../../speech-service-log/log";

/**
 * reference: @see {@link https://github.com/FairyDevicesRD/mimi.tagengo.examples/tree/master/src/JavaScript}
 *
 * mimi-asr-powered-by-nict api:
 * @see {@link https://mimi.readme.io/reference/mimi-asr-powered-by-nict}
 *
 * http header參數對應的websocket query string參考
 * @see {@link https://mimi.readme.io/docs/use-query}
 *
 */
export default class MimiAsr extends BaseAsr{
    /**
     * @type {AudioMedia}
     * @protected
     */
    _audioMedia;

    /**
     * @type {Websocket}
     * @protected
     */
    _socket;

    /**
     * mimi ASR websocket api的參數
     *
     * @type {Object}
     * @protected
     */
    _params = {};

    _isStop = false;

    _isRecogFinished = false;
    _isSentRecogBreak= false;

    _baseUrl = 'wss://service.mimi.fd.ai/';

    /**
     * blob緩衝佇列
     *
     * @type {Blob[]}
     * @protected
     */
    _blobs = [];

    /**
     * blob最大size(byte)
     *
     * reference: @see {@link https://mimi.readme.io/docs/mimi-websocket-api-spec#313-sending-audio-on-websocket}
     *
     * @type {number}
     * @protected
     */
    _blobMaxSize = 64000;

    /**
     * @type {Timer}
     * @protected
     */
    _socketSender;

    /**
     * socketSender消費間隔(ms)
     */
    _socketSenderInterval = 100;

    /**
     * blob產生間隔(ms)
     */
    _timeSlice = 100;

    constructor(params){
        super();

        this._socketSender = new Timer(() => {
            // 已發送 recog-break
            if(this._isSentRecogBreak) return;

            // 消費blobs
            while(this._blobs.length > 0){
                this._attemptSocketSend(this._blobs.shift());
            }
        }, this._socketSenderInterval);

        this._params = params;

        this._audioMedia = new AudioMedia(this._timeSlice);

        this._audioMedia.on('dataavailable', async (blob) => {
            if(this._blobs.length === 0){
                this._blobs.push(blob);
                return;
            }

            let lastBlob = this._blobs.pop();

            // 串接後的blob大小大於指定的大小
            if(lastBlob.size + blob.size > this._blobMaxSize){
                this._blobs.push(lastBlob, blob);
                return;
            }

            // blob串接
            lastBlob = new Blob([lastBlob, blob], {
                type: 'audio/pcm'
            });

            this._blobs.push(lastBlob);
        });

        this._audioMedia.on('stopped_speaking', () => {
            this._isSentRecogBreak = true;
            this._attemptSocketSend('{"command":"recog-break"}');
        });

        this._socket = this._createSocketWithBindEventHandler();
    }

    /**
     * 創建一個以綁定事件處理的websocket
     *
     * @returns {Websocket}
     */
    _createSocketWithBindEventHandler(){
        const socket = new WebSocket(this._generateSocketUrl());
        this._socketBindEventHandler(socket);
        return socket;
    }

    _generateSocketUrl(){
        return this._baseUrl + '?' + new URLSearchParams(this._params).toString();
    }

    /**
     * 把事件處理綁訂到websocket上
     *
     * @param {Websocket} socket
     */
    _socketBindEventHandler(socket){

        socket.onmessage = event => {
            // mimi ASR response
            const data = JSON.parse(event.data);
            const text = data?.response[0]?.result

            logMimiAsr(data);

            switch (data.status) {
                case "recog-in-progress":
                    if(!this._isRecogFinished)
                        this.emit("recognizing", text);
                    break;
                case "recog-finished":
                    this._isRecogFinished = true;
                    this.emit("recognized", text);
                    break;
              }
        };

        socket.onerror = event => {
            console.error(event);
            this._socket = this._createSocketWithBindEventHandler();
        };

        socket.onclose = () => {

            if (!this._isStop) { // 非主動關閉
                this._isRecogFinished = false;
                this._isSentRecogBreak = false;
                this._socket = this._createSocketWithBindEventHandler();
            }
        };
    }

    start(){
        this._isStop = false;

        if(this._audioMedia.state === AudioMedia.CLOSE)
            this._audioMedia.open();

        if(this._socketSender.state === Timer.STOP)
            this._socketSender.start();
    }

    stop(){
        this._isStop = true;

        this._audioMedia.close();
        this._socketSender.stop();
        this._socket.close();
        this.removeAllListeners();
    }

    _attemptSocketSend(data){
        // 已連線
        if(this._socket.readyState == 1)
            this._socket.send(data);
    }

    static fromToken(token, sourceLang, options =
        {
            'content-type': 'audio/x-pcm;bit=16;rate=44100;channels=1',
            'process': 'nict-asr',
            'nict-asr-options': 'response_format=v2;progressive=false;temporary=true;temporary_interval=75'
        }
    ){
        return new MimiAsr({
            ...{
                'access-token': token,
                'input-language': sourceLang,
            },
            ...options
        });
    }
}
